import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Facebook, Instagram, Mail, ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const socialLinks = [
  { icon: Linkedin, href: 'https://www.linkedin.com/in/ogaba-joseph-attah-6a7851118', label: 'LinkedIn' },
  { icon: Twitter, href: 'https://x.com/attah_meddyx', label: 'X/Twitter' },
  { icon: Facebook, href: 'https://www.facebook.com/attah.ogabajoseph', label: 'Facebook' },
  { icon: Instagram, href: 'https://www.instagram.com/jo_attah/', label: 'Instagram' },
];

const companyLinks = [
  { name: 'Nexintell', href: 'https://www.nexintell.co' },
  { name: 'Infralytics-GIIS', href: 'https://www.infralytics.co' },
  { name: 'Adoptic · Stockton Digital Register', href: 'https://adoptic.io' },
  { name: 'Evolve Digital Africa', href: 'https://www.evolvedigitalafrica.com.ng' },
  { name: 'Grit & Style', href: 'https://www.grit.ng' },
  { name: 'iEvolve CIC', href: 'https://www.ievolve.uk' },
  { name: 'Subterradynamics', href: 'https://www.subterradynamics.com' },
];

const siteLinks = [
  { name: 'About', path: '/about-ogaba-joseph-attah' },
  { name: 'Companies', path: '/companies' },
  { name: 'Media', path: '/media' },
  { name: 'Insights', path: '/insights' },
  { name: 'Speaking', path: '/speaking-advisory' },
  { name: 'Contact', path: '/contact' },
];

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="border-t border-border bg-card" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block" data-testid="footer-logo">
              <h3 className="text-2xl font-bold tracking-tight">Ogaba Joseph Attah</h3>
            </Link>
            <p className="mt-4 text-muted-foreground text-sm leading-relaxed">
              Technology Founder & Digital Infrastructure Intelligence Leader
            </p>
            <div className="flex gap-3 mt-6">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors"
                  data-testid={`footer-social-${social.label.toLowerCase()}`}
                  aria-label={social.label}
                >
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Site Links */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
              Navigation
            </h4>
            <ul className="space-y-3">
              {siteLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-foreground hover:text-primary transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Companies */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
              Companies & Products
            </h4>
            <ul className="space-y-3">
              {companyLinks.map((company) => (
                <li key={company.name}>
                  <a
                    href={company.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-foreground hover:text-primary transition-colors inline-flex items-center gap-1"
                    data-testid={`footer-company-${company.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {company.name}
                    <ArrowUpRight className="h-3 w-3" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact CTA */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
              Get in Touch
            </h4>
            <p className="text-sm text-muted-foreground mb-4">
              For speaking engagements, advisory work, or collaboration opportunities.
            </p>
            <Button asChild className="w-full" data-testid="footer-contact-btn">
              <Link to="/contact">
                <Mail className="h-4 w-4 mr-2" />
                Contact
              </Link>
            </Button>
          </div>
        </div>

        <div className="border-t border-border mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground">
            © {currentYear} Ogaba Joseph Attah. All rights reserved.
          </p>
          <p className="text-xs text-muted-foreground">
            Built with purpose. Designed for impact.
          </p>
        </div>
      </div>
    </footer>
  );
}
